import type { AxiosInstance } from "axios";

/**
 * The organization's team, as the Team screen sees it.
 *
 * Mirrors the API's `orgs` module (orgs.service.ts) rather than importing it — apps/web
 * cannot pull server code into a browser bundle, the same trade lib/integrations.ts makes.
 * The org is never named by the client: `/orgs/members` derives it from the current store,
 * so the header (X-Tenant-Id) the axios client already sends is all the scope there is.
 */
export type MemberRole = "owner" | "admin" | "analyst";

export interface Member {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly role: MemberRole;
  /** Null means org-wide; a value scopes them to one client's workspace. */
  readonly clientId: string | null;
  /** That client's name, for the roster. Null for org-wide. */
  readonly clientName?: string | null;
  /** INVITED until they redeem the emailed link and set a password; ACTIVE after. */
  readonly status: "invited" | "active";
  /** May switch the store's tracking off and on. Always true for an owner; never for an analyst. */
  readonly canControlTracking: boolean;
}

export interface Team {
  readonly members: readonly Member[];
  readonly seatsUsed: number;
  /** Null means the plan sets no seat limit — unlimited, not zero. */
  readonly seatsLimit: number | null;
}

export async function fetchTeam(client: AxiosInstance): Promise<Team> {
  const response = await client.get<{ success: boolean; data: Team }>("/orgs/members");
  return response.data.data;
}

export interface InviteInput {
  readonly email: string;
  readonly name?: string;
  readonly role: MemberRole;
  /** Scope the invitee to ONE client's stores. Absent = the whole organization. */
  readonly clientId?: string;
}

/** Which client workspaces the viewer may invite into, and whether they may invite org-wide at all. */
export interface InviteScopes {
  readonly orgWide: boolean;
  readonly clients: readonly { readonly id: string; readonly name: string }[];
}

/** An older API without `/orgs/clients` reads as "org-wide, no clients to choose" — the old form. */
export async function fetchInviteScopes(client: AxiosInstance): Promise<InviteScopes> {
  const response = await client.get<{ success: boolean; data: Partial<InviteScopes> | null }>("/orgs/clients");
  const data = response.data.data;
  return { orgWide: data?.orgWide !== false, clients: Array.isArray(data?.clients) ? data.clients : [] };
}

export interface InviteResult {
  readonly userId: string;
  readonly alreadyMember: boolean;
  readonly seatsUsed: number;
  readonly seatsLimit: number | null;
}

export async function inviteMember(client: AxiosInstance, input: InviteInput): Promise<InviteResult> {
  const response = await client.post<{ success: boolean; data: InviteResult }>("/orgs/invites", input);
  return response.data.data;
}

/** An owner grants or withdraws an admin's tracking control (Plugin Control). */
export async function setTrackingPermission(client: AxiosInstance, userId: string, enabled: boolean): Promise<void> {
  await client.post(`/orgs/members/${encodeURIComponent(userId)}/tracking-permission`, { enabled });
}

/** "3 of 5 seats used", or "3 seats used" when the plan sets no limit. */
export function seatSummary(used: number, limit: number | null): string {
  return limit === null ? `${String(used)} seats used` : `${String(used)} of ${String(limit)} seats used`;
}

/** At the seat limit — the invite form must refuse before the round-trip that would 402. */
export function seatsExhausted(used: number, limit: number | null): boolean {
  return limit !== null && used >= limit;
}
